import { useDispatch, useSelector } from "react-redux";
import { PolicyContractType , useSetProposalEffectiveDateMutation, useSubmitPolicyContractMutation, useUpdatePolicyDocCodeMutation, useUpdatePolicyMutation, useUpdatePolicySigningAddressMutation, useUpdatePolicyTATMutation } from "../../api/ebam/policy/policy-api";
import { resetPolicyDataPageState, selectPolicyType, selectProposalIdForPolicy, setIsForIssuancePolicy } from "../../features/ebam/plicy-data-page/policy-data-page.slice";
import { policyInformationType, resetPolicyInformation, setEffectiveDate, setPolicyInformationType } from "../../features/ebam/plicy-data-page/policy-information/policy-information.slice";
import { PolicyBody, PolicyTATBody, PolicyTATItem } from "../../api/ebam/policy/policy-body";
import { useToastify } from "../../library/toastify/toast-provider";
import { policyCoveragePremiumDetailsType, resetPolicyCoveragePremiumDetails, setPolicyCoveragePremiumDetailsType } from "../../features/ebam/plicy-data-page/policy-coverage-premium-details/policy-coverage-premium-details.slice";
import { policySpecialUnderwritingProvisionsTypes, setPolicySpecialUnderwritingProvisionsType, resetSpecialUnderWritingProvisionsState } from "../../features/ebam/plicy-data-page/policy-special-underwriting-provisions/policy-special-underwriting-provisions.slice";
import { resetPolicyTATState, setIsEditingTAT, setTATItems } from "../../features/ebam/plicy-data-page/policy-tat/policy-tat.slice";
import { usePolicyData } from "../populate-data/use-policy-data";
import { TATItems } from "../../features/ebam/plicy-data-page/policy-tat/policy-tat.interface";
import { usePolicyDataState } from "../policy/use-policy-data-state";

export const usePolicyActions = () => {

    const dispatch = useDispatch();
    const { showToastNotify } = useToastify();
    const { 
        populatePolicyInformation, 
        populateScheduleOfInsurance, 
        populateSigningAddressAndDocumentCode, 
        populateSpecialUnderwritingProvisions 
    } = usePolicyData()
    const {
        policyEligibleIndividual,
        policyContribution,
        policyParticipationRequirements,
        policyUnderwritingProvisions,
        policyUnderwritingProvisionsNotes,
        policySpecialUnderwritingProvisions,

        policyAmountInsurance,
        policyCoveragePeriod,
        policyDueDates,
        policyRefundOfPremiums,
        policyTerminationAge,
        policyDueDatesInfo,

        policyEnrollment,
        policyRollOverProvisions,
        policyTerminationOfIndividual,
        policyGeneralDefinitions,
        policyFaceAmountOfInsurance,
        policyPremiumComputation,
        policyClaimsProcedure,
        policyCoverageProvisions,
        policySpecialProvisions,

        policyFullAddress,
        policyDocumentCode,

        policyTATItems,

        policyEffectiveDate,
        policyId: proposalId,
        isPolicyTypeGPA_GADDP,
        contractType,
        contractTypeOutstanding,
        contractTypePrincipal,
        contractTypeGPA,

        notesForSubmitPolicyContract,
    } = usePolicyDataState()

    const [updatePolicy, { isLoading: isLoadingUpdatePolicy }] = useUpdatePolicyMutation();
    const [updateSigningAddress, { isLoading: isLoadingUpdateSigningAddress }] = useUpdatePolicySigningAddressMutation();
    const [updateDocumentCode, { isLoading: isLoadingUpdateDocumentCode }] = useUpdatePolicyDocCodeMutation();
    const [updateTAT, { isLoading: isLoadingUpdateTAT }] = useUpdatePolicyTATMutation();
    const [updatePolicyEffectiveDate, { isLoading: isLoadingUpdatePolicyEffectiveDate }] = useSetProposalEffectiveDateMutation();
    const [submitPolicyContract, { isLoading: isLoadingSubmitPolicyContract }] = useSubmitPolicyContractMutation();

    const handleEditPolicyInformation = async (type: policyInformationType) => {

        try {
            /* COMMON VALIDATION */
            if (proposalId === null) {
                showToastNotify("Proposal id is required", "warning");
                return;
            }
            if (!contractType) {
                showToastNotify("Policy type is required", "warning");
                return;
            }

            let body: PolicyBody | null = null;
            switch (type) {
                case "eligibleIndividuals": {
                    if (!policyEligibleIndividual) {
                        showToastNotify("Eligible Individuals is required", "warning");
                        return;
                    }
                    body = isPolicyTypeGPA_GADDP ? {
                        gpa_eligible_individuals: { eligible_individuals: policyEligibleIndividual, },
                    } : {
                        gclip_eligible_individuals: { eligible_individuals: policyEligibleIndividual, },
                    };
                    break;
                }
                case "contribution": {
                    // if (contractTypeGPA) {
                    //     showToastNotify("Underwriting provisions are not available for GPA", "warning");
                    //     return;
                    // }
                    if (!policyContribution) {
                        showToastNotify("Contribution is required", "warning");
                        return;
                    }
                    body = isPolicyTypeGPA_GADDP ? {
                        gpa_contribution: { contribution: policyContribution, },
                    } : {
                        gclip_contribution: { contribution: policyContribution, },
                    };
                    break;
                }
                case "participationRequirements": {
                    // if (contractTypeGPA) {
                    //     showToastNotify("Underwriting provisions are not available for GPA", "warning");
                    //     return;
                    // }
                    if (!policyParticipationRequirements) {
                        showToastNotify("Participation requirements are required", "warning");
                        return;
                    }
                    const percentage = policyParticipationRequirements.find((item) =>item.id === 1)?.amount ?? "";
                    const minimum_no  = policyParticipationRequirements.find((item) =>item.id === 2)?.amount ?? "";
                    if (!percentage) {
                        showToastNotify("Percentage of all Eligible Individuals is required", "warning");
                        return;
                    }
                    if (!minimum_no) {
                        showToastNotify("Minimum Number of Insureds is required", "warning");
                        return;
                    }
                    body = isPolicyTypeGPA_GADDP ? {
                        gpa_participation_requirements: { minimum_no: String(minimum_no), percentage: `${percentage}%`},
                    } : {
                        gclip_participation_requirements: { minimum_no: String(minimum_no), percentage: `${percentage}%`},
                    };
                    break;
                }
                case "underwritingProvisions": {
                    if (contractTypeGPA) {
                        showToastNotify("Underwriting provisions are not available for GPA", "warning");
                        return;
                    }
                    if (!policyUnderwritingProvisions) {
                        showToastNotify("Underwriting provisions are required", "warning");
                        return;
                    }

                    const nel = policyUnderwritingProvisions.find((item) => item.id === 1);
                    const nmed = policyUnderwritingProvisions.find((item) => item.id === 2);
                    const med = policyUnderwritingProvisions.find((item) => item.id === 3);
                    const maxLimit = policyUnderwritingProvisions.find((item) => item.id === 4);

                    body = {
                        gclip_underwriting_notes: {
                            nel: nel ? [{ amount: String(nel.amount ?? ""), age: String(nel.ageRange ?? "") }] : [],
                            nmed: nmed ? [{ amount: String(nmed.amount ?? ""), age: String(nmed.ageRange ?? "")}] : [],
                            med: med ? [{ amount: String(med.description ?? "") }] : [],
                            max_limit: maxLimit ? [{ amount: String(maxLimit.amount ?? ""), age: String(maxLimit.ageRange ?? "") }] : [],
                        },
                    };
                    break;
                }
                case "underwritingProvisionsNotes": {
                    if (contractTypeGPA) {
                        showToastNotify("Underwriting provisions are not available for GPA", "warning");
                        return;
                    }
                    if (!policyUnderwritingProvisionsNotes) {
                        showToastNotify("Underwriting provisions notes is required", "warning");
                        return;
                    }

                    body = {
                        gclip_underwriting_notes: {
                            underwriting_notes: policyUnderwritingProvisionsNotes,
                        },
                    };
                    break;
                }

                case "specialUnderwritingProvisions": {
                    if (contractType !== "gpa") {
                        showToastNotify("Special underwriting provisions are available only for GPA.", "warning");
                        return;
                    }
                    if (!policySpecialUnderwritingProvisions) {
                        showToastNotify("Special underwriting provisions notes is required", "warning");
                        return;
                    }

                    body = {
                        gpa_special_provisions: {
                            special_provisions: policySpecialUnderwritingProvisions,
                        },
                    };
                    break;
                }
                default:
                    showToastNotify("Invalid policy information type", "warning");
                    return;
            }

            if (!body) {
                return;
            }

            const response = await updatePolicy({ policyId: proposalId, type: contractType, body }).unwrap();
            showToastNotify(response.message, "success");
            dispatch(setPolicyInformationType(null));
        } catch (error) {
            showToastNotify("Failed to update Policy information", "error");
        }
    }

    const handleEditScheduleOfInsurance = async (type: policyCoveragePremiumDetailsType) => {

        try {
            /* COMMON VALIDATION */
            if (proposalId === null) {
                showToastNotify("Proposal id is required", "warning");
                return;
            }
            if (!contractType) {
                showToastNotify("Policy type is required", "warning");
                return;
            }

            let body: PolicyBody | null = null;
            switch (type) {
                // GCLIP_PRIN, GCLIP_OUT
                case "amountOfInsurance": {
                    if (!contractTypePrincipal && !contractTypeOutstanding) {
                        showToastNotify("Amount of Insurance is only available for GCLIP", "warning");
                        return;
                    }
                    if (!policyAmountInsurance) {
                        showToastNotify("Amount of Insurance is required", "warning");
                        return;
                    }
                    body = {
                        gclip_schedule_of_insurance: { amount_of_insurance: policyAmountInsurance, }
                    };
                    break;
                }
                // GCLIP_PRIN, GCLIP_OUT
                case "coveragePeriod": {
                    if (!contractTypePrincipal && !contractTypeOutstanding) {
                        showToastNotify("Coverage period is only available for GCLIP", "warning");
                        return;
                    }
                    if (!policyCoveragePeriod) {
                        showToastNotify("Coverage period is required", "warning");
                        return;
                    }
                    body = {
                        gclip_schedule_of_insurance: { coverage_period: policyCoveragePeriod, },
                    };
                    break;
                }
                // GCLIP_PRIN, GCLIP_OUT
                case "dueDates": {
                    if (!contractTypePrincipal && !contractTypeOutstanding) {
                        showToastNotify("Coverage period is only available for GCLIP", "warning");
                        return;
                    }
                    if (!policyDueDates) {
                        showToastNotify("Due dates is required", "warning");
                        return;
                    }
                    body = {
                        gclip_schedule_of_insurance: { due_dates: policyDueDates, },
                    };
                    break;
                }
                // GCLIP_PRIN, GCLIP_OUT
                case "refundOfPremiums": {
                    if (!contractTypePrincipal && !contractTypeOutstanding) {
                        showToastNotify("Refund of Premiums is only available for GCLIP", "warning");
                        return;
                    }
                    if (!policyRefundOfPremiums) {
                        showToastNotify("Refund of Premiums is required", "warning");
                        return;
                    }
                    body = {
                        gclip_schedule_of_insurance: { refund_of_premiums: policyRefundOfPremiums, },
                    };
                    break;
                }
                // GCLIP_PRIN, GCLIP_OUT
                case "terminationAge": {
                    if (!contractTypePrincipal && !contractTypeOutstanding) {
                        showToastNotify("Termination age is only available for GCLIP", "warning");
                        return;
                    }
                    if (!policyTerminationAge) {
                        showToastNotify("Coverage period is required", "warning");
                        return;
                    }
                    body = {
                        gclip_schedule_of_insurance: { termination_age: policyTerminationAge, },
                    };
                    break;
                }
                // GPA_GADDP
                case "dueDatesInfo": {
                    if (contractTypeGPA) {
                        showToastNotify("Termination age is only available for GPA_GADDP", "warning");
                        return;
                    }
                    if (!policyDueDatesInfo.additions_due_date || !policyDueDatesInfo.first_due_date || !policyDueDatesInfo.renewal_due_date) {
                        showToastNotify("Due dates is required", "warning");
                        return;
                    }
                    body = {
                        gpa_schedule_of_insurance: { 
                            first_due_date: policyDueDatesInfo.first_due_date,
                            renewal_due_date: policyDueDatesInfo.renewal_due_date,
                            additions_due_date: policyDueDatesInfo.additions_due_date,
                        },
                    };
                    break;
                }
                default:
                    showToastNotify("Invalid schedule of insurance type", "warning");
                    return;
            }
            if (!body) {
                return;
            }
            const response = await updatePolicy({ policyId: proposalId, type: contractType, body }).unwrap();
            showToastNotify(response.message, "success");
            dispatch(setPolicyCoveragePremiumDetailsType(null));
        } catch (error) {
            showToastNotify("Failed to update schedule of insurance", "error");
        }
    }

    const handleEditSpecialUnderWritingProvisions = async (type: policySpecialUnderwritingProvisionsTypes) => {
        try {
            /* COMMON VALIDATION */
            if (proposalId === null) {
                showToastNotify("Proposal id is required", "warning");
                return;
            }
            if (!contractType) {
                showToastNotify("Policy type is required", "warning");
                return;
            }

            let body: PolicyBody | null = null;
            switch (type) {
                case "enrollment": {
                    if (!contractTypePrincipal && !contractTypeOutstanding) {
                        showToastNotify("Enrollment is only available for GCLIP", "warning");
                        return;
                    }
                    if (!policyEnrollment) {
                        showToastNotify("Enrollment is required", "warning");
                        return;
                    }
                    body = {
                        gclip_special_provision: { enrollment: policyEnrollment }
                    };
                    break;
                }
                case "rollOverProvisions": {
                    if (!contractTypePrincipal && !contractTypeOutstanding) {
                        showToastNotify("Roll-over provisions is only available for GCLIP", "warning");
                        return;
                    }
                    if (!policyRollOverProvisions) {
                        showToastNotify("Roll-over provisions is required", "warning");
                        return;
                    }
                    body = {
                        gclip_special_provision: { rollover_provision: policyRollOverProvisions }
                    };
                    break;
                }
                case "terminationOfIndividual": {
                    if (!contractTypePrincipal && !contractTypeOutstanding) {
                        showToastNotify("Termination of insurance is only available for GCLIP", "warning");
                        return;
                    }
                    if (!policyTerminationOfIndividual) {
                        showToastNotify("Termination of insurance is required", "warning");
                        return;
                    }
                    body = {
                        gclip_special_provision: { termination_of_insurance: policyTerminationOfIndividual }
                    };
                    break;
                }
                case "generalDefinitions": {
                    if (!contractTypePrincipal && !contractTypeOutstanding) {
                        showToastNotify("General definitions is only available for GCLIP", "warning");
                        return;
                    }
                    if (!policyGeneralDefinitions) {
                        showToastNotify("General definitions is required", "warning");
                        return;
                    }
                    body = {
                        gclip_special_provision: { general_definitions: policyGeneralDefinitions }
                    };
                    break;
                }
                case "faceAmountOfInsurance": {
                    if (!contractTypePrincipal && !contractTypeOutstanding) {
                        showToastNotify("Face amount of insurance is only available for GCLIP", "warning");
                        return;
                    }
                    if (!policyFaceAmountOfInsurance) {
                        showToastNotify("Face amount of insurance is required", "warning");
                        return;
                    }
                    body = {
                        gclip_special_provision: { face_amount_insurance: policyFaceAmountOfInsurance }
                    };
                    break;
                }
                case "premiumComputation": {
                    if (!contractTypePrincipal && !contractTypeOutstanding) {
                        showToastNotify("Premium computation is only available for GCLIP", "warning");
                        return;
                    }
                    if (!policyPremiumComputation) {
                        showToastNotify("Premium computation is required", "warning");
                        return;
                    }
                    body = {
                        gclip_special_provision: { premium_computation: policyPremiumComputation }
                    };
                    break;
                }
                case "nonCoverageProvisions": {
                    if (!contractTypePrincipal && !contractTypeOutstanding) {
                        showToastNotify("Non coverage provisions is only available for GCLIP", "warning");
                        return;
                    }
                    if (!policyCoverageProvisions) {
                        showToastNotify("Non coverage provisions is required", "warning");
                        return;
                    }
                    body = {
                        gclip_special_provision: { non_coverage_provision: policyCoverageProvisions }
                    };
                    break;
                }
                case "claimsProcedure": {
                    if (!contractTypePrincipal && !contractTypeOutstanding) {
                        showToastNotify("Claims procedure is only available for GCLIP", "warning");
                        return;
                    }
                    if (!policyClaimsProcedure) {
                        showToastNotify("Claims procedure is required", "warning");
                        return;
                    }
                    body = {
                        gclip_special_provision: { claims_procedure: policyClaimsProcedure }
                    };
                    break;
                }
                case "specialProvisions": {
                    if (contractTypeGPA) {
                        showToastNotify("Special provisions is only available for GCLIP", "warning");
                        return;
                    }
                    if (!policySpecialProvisions) {
                        showToastNotify("Special provisions is required", "warning");
                        return;
                    }
                    body = {
                        gpa_special_provisions: { special_provisions: policySpecialProvisions }
                    };
                    break;
                }
                default:
                    showToastNotify("Invalid special underwriting provisions type", "warning");
                    return;
            }
            const response = await updatePolicy({ policyId: proposalId, type: contractType, body }).unwrap();
            showToastNotify(response.message, "success");
            dispatch(setPolicySpecialUnderwritingProvisionsType(null));
        } catch (error) {
            showToastNotify("Failed to update schedule of insurance", "error");
        }
    }

    const handleEditSigningAddress = async () => {
        try {
            if (proposalId === null) {
                showToastNotify("Proposal id is required", "warning");
                return;
            }
            if (!contractType) {
                showToastNotify("Policy type is required", "warning");
                return;
            }
            if (!policyFullAddress) {
                showToastNotify("Signing address is required", "warning");
                return;
            }
            const body: PolicyBody = {
                signing_location: policyFullAddress
            }
            const response = await updateSigningAddress({ policyId: proposalId, type: contractType, body }).unwrap();
            showToastNotify(response.message, "success");
            dispatch(setPolicyInformationType(null));
        } catch (error) {
            showToastNotify("Failed to update signing address", "error");
        }
    }
    
    const handleEditDocumentCode = async () => {
        try {
            if (proposalId === null) {
                showToastNotify("Proposal id is required", "warning");
                return;
            }
            if (!contractType) {
                showToastNotify("Policy type is required", "warning");
                return;
            }
            if (!policyDocumentCode) {
                showToastNotify("Document code is required", "warning");
                return;
            }
            const body: PolicyBody = {
                doc_code: policyDocumentCode
            }
            const response = await updateDocumentCode({ policyId: proposalId, type: contractType, body }).unwrap();
            showToastNotify(response.message, "success");
            dispatch(setPolicyInformationType(null));
        } catch (error) {
            showToastNotify("Failed to update document code", "error");
        }
    }
    
    const handleEditPolicyTAT = async () => {
        try {
            if (proposalId === null) {
                showToastNotify("Proposal id is required", "warning");
                return;
            }
            const items: PolicyTATItem[] = policyTATItems?.map((item) => ({
                sort_order: item.sort_order,
                category: item.category,
                activity: item.activity,
                tat: item.tat,
                reckoning_date: item.reckoning_date,
                mode_of_communication: item.mode_of_communication,
                responsible: item.responsible,
                contact_details: item.contact_details,
            })) ?? [];
            if (items.length === 0) {
                showToastNotify("No TAT items to update", "warning");
                return;
            }
            const body: PolicyTATBody = {
                items: items
            }
            const response = await updateTAT({ policyId: proposalId, body }).unwrap();
            dispatch(resetPolicyTATState());
            showToastNotify(response.message, "success");
        } catch (error) {
            showToastNotify("Failed to update turn-around time", "error");
        }
    }

    const handleUpdatePolicyEffectiveDate = async () => {
        try {
            if(!policyEffectiveDate.value) {
                showToastNotify('Enter a valid effective date', 'warning')
                return
            }
            if(!proposalId || proposalId === null) {
                showToastNotify('Proposal id is required', 'warning')
                return
            }
            const body = { proposalId, base_date: policyEffectiveDate.value}
            const response = await updatePolicyEffectiveDate(body).unwrap()
            showToastNotify(response.message, 'success')
            dispatch(setEffectiveDate({ isEdit: false }));
        } catch (error) {
            showToastNotify("Failed to update effective date.", "error");
        }
    }

    const handleIssuedPolicyContract = async (id: number) => {
        try {
            if(!id || id === null || id === undefined) {
                showToastNotify('Proposal id is required', 'warning')
                return 
            }
            const notes = notesForSubmitPolicyContract.length === 0 ? null : notesForSubmitPolicyContract
            const response = await submitPolicyContract({policyId: id, notes}).unwrap();
            dispatch(setIsForIssuancePolicy({
                isIssuancePolicy: false,
                notesForPolicyApproval: '',
            }))
            showToastNotify(response.message, 'success')
        } catch (error: any) {
            const errMsg = error?.message
            const dftMsg = `Failed to submit policy contract.`
            const message = errMsg || dftMsg
            showToastNotify(message, "error");
        }
    }

    const handleCancelEditEffectiveDate = () => {
        dispatch(setEffectiveDate({ isEdit: false }));
        populatePolicyInformation()
    }

    const handleCancelEditPolicyInformation = () => {
        populatePolicyInformation()
        dispatch(setPolicyInformationType(null));
    }

    const handleCancelEditPolicySchedule = () => {
        populateScheduleOfInsurance()
        dispatch(setPolicyCoveragePremiumDetailsType(null));
    }

    const handleCancelEditPolicyUnderwritingProvisions = () => {
        populateSpecialUnderwritingProvisions()
        dispatch(setPolicySpecialUnderwritingProvisionsType(null));
    }

    const handleCancelEditPolicyAddressAndDocCode = () => {
        populateSigningAddressAndDocumentCode()
        dispatch(setPolicyInformationType(null))
    }

    const handleEditTAT = (item: TATItems[]) => {
        dispatch(setTATItems(structuredClone(item)));
        dispatch(setIsEditingTAT(true));
    };

    const onClosePolicyModal = () => {
        dispatch(resetPolicyInformation());
        dispatch(resetSpecialUnderWritingProvisionsState());
        dispatch(resetPolicyCoveragePremiumDetails());
        dispatch(resetPolicyTATState());
        dispatch(resetPolicyDataPageState());
    };

    return {

        onClosePolicyModal,
        handleEditTAT,

        handleCancelEditPolicyInformation,
        handleCancelEditPolicySchedule,
        handleCancelEditPolicyUnderwritingProvisions,
        handleCancelEditPolicyAddressAndDocCode,

        handleEditPolicyInformation,
        handleEditScheduleOfInsurance,
        handleEditSpecialUnderWritingProvisions,
        isLoadingUpdatePolicy,
        
        handleEditSigningAddress,
        isLoadingUpdateSigningAddress,

        handleEditDocumentCode,
        isLoadingUpdateDocumentCode,

        handleEditPolicyTAT,
        isLoadingUpdateTAT,

        handleCancelEditEffectiveDate,
        handleUpdatePolicyEffectiveDate,
        isLoadingUpdatePolicyEffectiveDate,

        handleIssuedPolicyContract,
        isLoadingSubmitPolicyContract,
    };
};